import { getDefaultProctoringOption, ThemeWrapper } from "@verity/components";
import { CoreButton, CoreStepper, CoreToastContainer } from "@verity/components/core";
import { ProctoringOption } from "@verity/proctoring-option/types";
import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "../../../../i18n";
import { PortalControllerContext } from "../../../context";
import EditSubmitButton from "./components/EditSubmitButton";
import SubmitButton from "./components/SubmitButton";
import {
	defaultConferencePlatform,
	defaultConferencePlatformErrors,
	defaultInterviewDetailsErrors,
	getDefaultInterviewDetails,
} from "./defaults";
import ConferencePlatformForm from "./forms/ConferencePlatformForm";
import Configurations from "./forms/Configurations";
import InterviewDetailsForm from "./forms/InterviewDetailsForm";
import useStepCounter from "./hooks/useStepReducer";
import ScheduleInterviewLayout from "./layouts/ScheduleInterviewLayout";
import InterviewConfirmationPage from "./pages/ConfirmationPage/ConfirmationPage";
import {
	buildEmptyValidator,
	ConferencePlatformErrorsType,
	ConferencePlatformType,
	ConferencePlatformValidatorsType,
	InterviewDetailsErrorsType,
	InterviewDetailsType,
	InterviewDetailsValidatorsType,
} from "./types";
import {
	validateEmail,
	validateFutureDate,
	validateRequiredList,
	validateRequiredString,
	validateUrl,
} from "./validators";

type FormBundle<T> = {
	values: T;
	setValues: React.Dispatch<React.SetStateAction<T>>;
	errors: { [K in keyof T]: string };
	setErrors: React.Dispatch<React.SetStateAction<{ [K in keyof T]: string }>>;
	validators: { [K in keyof T]: (value: T[K]) => string };
};

type ExtractFormBundleType<T> = T extends FormBundle<infer U> ? U : never;

function applyErrors<T>(
	formValues: T,
	validators: { [K in keyof T]: (value: T[K]) => string },
): { [K in keyof T]: string } {
	const errors = {} as { [K in keyof T]: string };
	for (const key in formValues) {
		errors[key] = validators[key as keyof T](formValues[key as keyof T]);
	}
	return errors;
}

function validateBundle<T>(bundle: FormBundle<T>) {
	const errors = applyErrors(bundle.values, bundle.validators);
	bundle.setErrors(errors);
	return Object.values(errors).some((error) => error !== "");
}

type LocationState = {
	assessmentId?: number;
	assessmentUuid?: string;
	interviewDetails?: InterviewDetailsType;
	conferencePlatform?: ConferencePlatformType;
	proctoringOption?: Partial<ProctoringOption>;
};

export default function ScheduleInterview() {
	const { verityInstituteSettings } = useContext(PortalControllerContext);

	const navigate = useNavigate();
	const locationState = useLocation().state as LocationState | undefined;
	const {
		assessmentId,
		assessmentUuid,
		interviewDetails: oldInterviewOptions,
		conferencePlatform: oldConferencePlatform,
		proctoringOption: oldProctoringOption,
	} = locationState || {};

	const isEdit =
		!!assessmentId && !!assessmentUuid && !!oldInterviewOptions && !!oldConferencePlatform && !!oldProctoringOption;

	const [interviewDetailsForm, setInterviewDetailsForm] = useState<InterviewDetailsType>(
		oldInterviewOptions || getDefaultInterviewDetails(),
	);
	const [interviewDetailsErrors, setInterviewDetailsErrors] =
		useState<InterviewDetailsErrorsType>(defaultInterviewDetailsErrors);

	const interviewDetailsFormValidators: InterviewDetailsValidatorsType = {
		title: validateRequiredString,
		candidateName: validateRequiredString,
		candidateEmail: validateEmail,
		positionName: validateRequiredString,
		interviewerUuids: validateRequiredList,
		startTime: validateFutureDate,
		endTime: validateFutureDate,
	};

	const interviewDetailsFormBundle: FormBundle<InterviewDetailsType> = {
		values: interviewDetailsForm,
		setValues: setInterviewDetailsForm,
		errors: interviewDetailsErrors,
		setErrors: setInterviewDetailsErrors,
		validators: interviewDetailsFormValidators,
	};

	const [conferencePlatformForm, setConferencePlatformForm] = useState<ConferencePlatformType>(
		oldConferencePlatform || defaultConferencePlatform,
	);
	const [conferencePlatformErrors, setConferencePlatformErrors] = useState<ConferencePlatformErrorsType>(
		defaultConferencePlatformErrors,
	);

	const conferencePlatformFormValidators: ConferencePlatformValidatorsType = {
		videoPlatform: validateRequiredString,
		meetingLink: validateUrl,
		notes: () => "",
	};

	const conferencePlatformFormBundle: FormBundle<ConferencePlatformType> = {
		values: conferencePlatformForm,
		setValues: setConferencePlatformForm,
		errors: conferencePlatformErrors,
		setErrors: setConferencePlatformErrors,
		validators: conferencePlatformFormValidators,
	};

	const [proctoringOption, setProctoringOption] = useState<Partial<ProctoringOption>>(
		oldProctoringOption || getDefaultProctoringOption(verityInstituteSettings),
	);

	const proctoringOptionBundle: FormBundle<Partial<ProctoringOption>> = {
		values: proctoringOption,
		setValues: setProctoringOption,
		errors: {},
		setErrors: () => {},
		validators: buildEmptyValidator(proctoringOption),
	};

	const STEPS = [
		{ key: "details", label: i18n.t(`candidate.scheduleInterview.steps.details`) },
		{ key: "platform", label: i18n.t(`candidate.scheduleInterview.steps.platform`) },
		{ key: "configurations", label: i18n.t(`candidate.scheduleInterview.steps.configurations`) },
		{
			key: "configurations.record",
			label: i18n.t(`candidate.scheduleInterview.steps.record`),
			parentSubstep: "configurations",
		},
		{
			key: "configurations.verification",
			label: i18n.t(`candidate.scheduleInterview.steps.verification`),
			parentSubstep: "configurations",
		},
		{
			key: "configurations.disableFeatures",
			label: i18n.t(`candidate.scheduleInterview.steps.disableFeatures`),
			parentSubstep: "configurations",
		},
		{
			key: "configurations.otherActivities",
			label: i18n.t(`candidate.scheduleInterview.steps.otherActivities`),
			parentSubstep: "configurations",
		},
		{
			key: "configurations.showTools",
			label: i18n.t(`candidate.scheduleInterview.steps.tools`),
			parentSubstep: "configurations",
		},
		{ key: "review", label: i18n.t(`candidate.scheduleInterview.steps.review`) },
	];

	const { state: stepState, onNext, onBack, goToStep } = useStepCounter(STEPS, isEdit);

	const PAGES = [
		{
			key: "details",
			bundle: interviewDetailsFormBundle,
			render: () => (
				<InterviewDetailsForm
					formValues={interviewDetailsForm}
					setFormValues={setInterviewDetailsForm}
					errors={interviewDetailsErrors}
					setErrors={setInterviewDetailsErrors}
				/>
			),
		},
		{
			key: "platform",
			bundle: conferencePlatformFormBundle,
			render: () => (
				<ConferencePlatformForm
					formValues={conferencePlatformForm}
					setFormValues={setConferencePlatformForm}
					errors={conferencePlatformErrors}
					setErrors={setConferencePlatformErrors}
				/>
			),
		},
		{
			key: "configurations",
			bundle: proctoringOptionBundle,
			render: (subpage: string) => (
				<Configurations
					proctoringOption={proctoringOption}
					setProctoringOption={setProctoringOption}
					subpage={subpage}
				/>
			),
		},
		{
			key: "review",
			// bundle: {} as FormBundle<undefined>,
			render: () => (
				<InterviewConfirmationPage
					interviewDetails={interviewDetailsForm}
					conferencePlatform={conferencePlatformForm}
					proctoringOptions={proctoringOption}
					goToStep={goToStep}
				/>
			),
		},
	];

	const [currentPageKey, currentSubpageKey] = stepState.currentStepKey.split(".").slice(0, 2);

	function handleNext() {
		const currentPage = PAGES.find((p) => p.key === currentPageKey);
		if (!currentPage || !currentPage.bundle) return;

		type BundleType = ExtractFormBundleType<typeof currentPage.bundle>;
		if (!validateBundle(currentPage.bundle as FormBundle<BundleType>)) {
			onNext();
		}
	}

	function handleGoToStep(idx: number) {
		const currentPage = PAGES.find((p) => p.key === currentPageKey);
		if (!currentPage) return;

		type BundleType = ExtractFormBundleType<typeof currentPage.bundle>;
		if (
			PAGES.indexOf(currentPage) === PAGES.length - 1 ||
			!validateBundle(currentPage.bundle as FormBundle<BundleType>)
		) {
			goToStep(idx);
		}
	}

	const stepper = (
		<CoreStepper
			steps={STEPS}
			onStepChange={handleGoToStep}
			activeStep={stepState.currentStepIdx}
			completedSteps={stepState.completedSteps}
			vertical
		/>
	);

	const {
		startTime,
		endTime,
		interviewerUuids: interviewerIds,
		...candidateOptions
	} = {
		...interviewDetailsForm,
		...conferencePlatformForm,
	};

	const nextButton =
		stepState.currentStepIdx === STEPS.length - 1 ? (
			isEdit ? (
				<EditSubmitButton
					assId={assessmentId}
					assUuid={assessmentUuid}
					candidateOptions={candidateOptions}
					proctoringOptions={proctoringOption}
					sessionDates={{
						start: startTime,
						end: endTime,
					}}
					interviewerUuids={interviewerIds}
				/>
			) : (
				<SubmitButton
					candidateOptions={candidateOptions}
					proctoringOptions={proctoringOption}
					sessionDates={{
						start: startTime,
						end: endTime,
					}}
					interviewerUuids={interviewerIds}
				/>
			)
		) : (
			<CoreButton onClick={handleNext}>{i18n.t(`candidate.scheduleInterview.navigation.next`)}</CoreButton>
		);

	const backButton =
		stepState.currentStepIdx === 0 ? (
			<CoreButton onClick={() => navigate("/interviews")}>
				{i18n.t(`candidate.scheduleInterview.navigation.cancel`)}
			</CoreButton>
		) : (
			<CoreButton onClick={onBack}>{i18n.t(`candidate.scheduleInterview.navigation.back`)}</CoreButton>
		);

	return (
		<ThemeWrapper scoped={false}>
			<ScheduleInterviewLayout stepper={stepper} nextButton={nextButton} backButton={backButton}>
				{PAGES.find((p) => p.key === currentPageKey)?.render(currentSubpageKey)}
			</ScheduleInterviewLayout>
			<CoreToastContainer />
		</ThemeWrapper>
	);
}
